import React from 'react'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { useLangContext } from '../context/lang.context'
import { colors, alpha, s } from '../style'
import Logo from './Logo'
import Button from './Button'
import H from './H'
import ReactPlayer from 'react-player/lazy'
import alt from '../content/alt'

const Intro = ({
  data,
  image,
  video,
  logo,
  h,
  rounded,
  title: titleProp,
  desc: descProp,
  button: buttonProp,
  buttonSecond: buttonSecondProp,
  titleAnimated,
}) => {
  const scrollDown = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({
        top: rounded ? window.innerHeight + 96 : window.innerHeight,
        behavior: 'smooth',
      })
    }
  }

  const { lang } = useLangContext()
  const title = data ? data.title?.[lang] : titleProp
  const desc = data ? data.desc[lang] : descProp
  const button = data?.button
    ? {
        text: data.button.text[lang],
        link: data.button.link,
        action:
          data.button.action === 'SCROLL' ? scrollDown : data.button.action,
      }
    : buttonProp
    ? {
        ...buttonProp,
        action: buttonProp.action === 'SCROLL' ? scrollDown : buttonProp.action,
      }
    : null

  const buttonSecond = data?.buttonSecond
    ? {
        text: data.buttonSecond.text[lang],
        link: data.buttonSecond.link,
        action:
          data.buttonSecond.action === 'SCROLL'
            ? scrollDown
            : data.buttonSecond.action,
      }
    : buttonSecondProp
    ? {
        ...buttonSecondProp,
        action:
          buttonSecondProp.action === 'SCROLL'
            ? scrollDown
            : buttonSecondProp.action,
      }
    : null

  const path = typeof window !== 'undefined' ? window.location.pathname : ''

  return (
    <section
      css={[
        sSection,
        rounded && {
          height: 'calc(100vh + 2rem)',
          borderRadius: '0 0 2rem 2rem',
          overflow: 'hidden',
          [s.md]: {
            borderRadius: '0 0 6rem 6rem',
            height: 'calc(100vh + 6rem)',
          },
        },
      ]}>
      {image && (
        <div css={[sImageWrapper]}>
          <GatsbyImage
            image={getImage(image)}
            style={sImageWrapper.style}
            imgStyle={sImageWrapper.imgStyle}
            alt={title + alt}
          />
        </div>
      )}
      {video && (
        <div css={sReactPlayerWrapper}>
          <ReactPlayer
            className='react-player'
            url={video}
            autoPlay
            loop
            muted
            width='100%'
            height='100%'
            playsinline
            playing
            config={{
              youtube: {
                playerVars: {
                  showinfo: 0,
                  modestbranding: 1,
                  rel: 0,
                  frameborder: 0,
                  disablekb: 1,
                },
              },
            }}
          />
        </div>
      )}
      <div css={sDimmer} />
      <div css={[sTextWrapper]}>
        <div css={[sTextContainer, logo && sTextContainerLogo]}>
          <div css={sRelative}>
            {logo && (
              <span css={sLogo}>
                <Logo />
              </span>
            )}
            {data?.titleHtml ? (
              <H h={h} css={sTitle}>
                <span
                  dangerouslySetInnerHTML={{ __html: data.titleHtml[lang] }}
                />
              </H>
            ) : (
              title && (
                <H
                  h={h}
                  css={[
                    sTitle,
                    logo && sTitleLogo,
                    title
                      .split(' ')
                      .reduce(
                        (sum, curr) => sum || curr.length > 10,
                        false
                      ) && { [s.xs]: { lineHeight: '1em' } },
                  ]}>
                  <ResponsiveFont text={title} />
                </H>
              )
            )}
            {titleAnimated && <H css={sTitle}>{titleAnimated}</H>}
          </div>
          <p css={sDesc} dangerouslySetInnerHTML={{ __html: desc }} />
          <div
            css={{
              display: 'flex',
              [s.xs]: { flexDirection: 'column', width: '100%' },
            }}>
            {button &&  (
              <Button
                noMargin
                trade
                action={button.action}
                link={
                  typeof button.link === 'object'
                    ? button.link[lang]
                    : button.link
                }
                extraCss={{
                  [s.sm_down]: { marginBottom: '1rem' },
                  [s.md]: { marginRight: '2rem' },
                }}>
                {button.text}
              </Button>
            )}
            {buttonSecond && (
              <Button
                noMargin
                dimmedWhite
                action={buttonSecond.action}
                link={
                  typeof buttonSecond.link === 'object'
                    ? buttonSecond.link[lang]
                    : buttonSecond.link
                }
                extraCss={{
                  [s.xs]: {
                    backgroundColor: colors.text.concat(alpha[100]),
                    color: colors.white,
                  },
                }}>
                {buttonSecond.text}
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

const ResponsiveFont = ({ text }) => {
  const texts = text.split(' ')

  const setFont = (v) => ({
    [s.xs]: { fontSize: v + 0.25 + 'rem' },
    [s.ss]: { fontSize: v + 'rem' },
  })

  return texts.map((word, id) => (
    <span
      key={id}
      css={[
        word.length >= 11 && setFont(2.75),
        word.length >= 12 && setFont(2.5),
        word.length >= 13 && setFont(2.25),
        word.length >= 14 && setFont(2),
      ]}>
      {word}{' '}
    </span>
  ))
}

const sTitle = {
  color: 'white',
  fontWeight: 900,
  // textTransform: 'capitalize',
  textAlign: 'left',
  [s.xs]: {
    fontSize: '2.75rem',
    letterSpacing: '-0.05em',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    maxWidth: 'calc(100vw - 4rem)',
    // whiteSpace: 'break-all',
    lineHeight: '1.15em',
  },
  [s.ss]: {
    fontSize: '2.5rem',
  },
  [s.sm]: {
    fontSize: '2.75rem',
    letterSpacing: '-0.05em',
    lineHeight: '1.35em',
  },
  [s.md]: {
    fontSize: '4rem',
  },
  [s.lg]: {
    fontSize: '5rem',
    lineHeight: '1em',
  },
}

const sTitleLogo = {
  fontFamily: 'Bank Gothic',
  textTransform: 'uppercase',
  fontWeight: 400,
  lineHeight: '1em',

  [s.xs]: {
    fontSize: '2.5rem',
    letterSpacing: '0em',
  },
  [s.sm]: {
    fontSize: '5rem',
    letterSpacing: '0em',
  },
  [s.lg]: {
    fontSize: '6rem',
    letterSpacing: '0em',
  },
}

const sRelative = {
  position: 'relative',
  top: 0,
  left: 0,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  width: '100%',
}

const sDesc = {
  color: 'white',
  lineHeight: '1.618em',
  padding: '6rem 0',
  fontSize: '1rem',
  letterSpacing: '0.16em',
  opacity: 0.8,
  [s.xs]: {
    width: '100%',
    opacity: 1,
    padding: '2rem 0',
    textAlign: 'left',
    textShadow: '1px 1px 2px rgba(0,0,0, 0.3)',
    fontSize: '0.875rem',
    lineHeight: '2em',
  },
  [s.ss]: {
    padding: '0 0 2rem',
  },
  [s.lg]: {
    width: '50%',
  },
}

const sSection = {
  width: '100vw',
  height: '100vh',
  position: 'relative',
  minHeight: 660,
  backgroundColor: colors.text,
  overflowX: 'hidden',
  overflowY: 'hidden',
}

const sImageWrapper = {
  position: 'relative',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,

  style: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    // top: '50%',
    // right: '50%',
    // transform: 'translate(-50%, -50%)',
    // objectFit: 'cover',
  },

  imgStyle: {
    objectFit: 'cover',
    objectPosition: 'center center',
  },
}

const sReactPlayerWrapper = {
  '@media (min-aspect-ratio: 16/9)': {
    height: 'calc(900vw / 16)',
    width: '100vw',
  },
  // mobile
  '@media (max-aspect-ratio: 16/9)': {
    height: '100vh',
    width: 'calc(1600vh / 9)',
  },
  // width: 'calc(1600vh / 9)',
  // height: '100vh',
  position: 'relative',
  top: '50%',
  left: '50%',
  margin: '0 auto',
  transform: 'translate(-50%, -50%)',
  '.react-player': {
    transition: 'opacity 0.3s ease-out',
    pointerEvents: 'none',
    objectFit: 'cover',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '100%',
    height: '100%',
    // display: 'block',
    transform: 'translate(-50%, -50%)',
    '@supports (not(grid-template-rows))': {
      [s.lg]: {
        minWidth: '115vw',
        // minHeight: '100vh',
      },
    },

    'video, iframe': {
      objectFit: 'cover',
    },
  },
}

const sTextWrapper = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  zIndex: 100,
  transform: 'translate(-50%, -50%)',
  width: '100vw',
  maxWidth: 1600,
}

const sLogo = {
  position: 'absolute',
  top: 0,
  left: 8,
  zIndex: 1,
  transform: 'translateY(-100%)',
  color: 'white',
  [s.xs]: {
    left: 4,
  },
  // svg: {
  //   maxWidth: 'calc(100vw - 64px)',
  // },
}

const sTextContainer = {
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  height: '100vh',
  width: '100%',
  maxWidth: 1500,
  margin: '0 auto',
  padding: '0 2rem',

  [s.sm_down]: {
    paddingTop: '8rem',
    paddingBottom: '4rem',
  },
  [s.xs]: {
    alignItems: 'center',
    textAlign: 'center',
    justifyContent: 'space-between',
  },
  [s.ss]: {
    paddingTop: '6rem',
  },
  [s.sm]: {
    paddingTop: '5rem',
    minHeight: 660,
  },
  [s.md]: {
    padding: '0 4rem',
  },
  [s.lg]: {
    padding: '0 4rem',
    // paddingLeft: '3rem',
  },
}

const sTextContainerLogo = {
  [s.sm_down]: {
    paddingTop: '10rem',
  },
  [s.ss]: {
    paddingTop: '8rem',
    paddingBottom: '2rem',
  },
}

const sDimmer = {
  position: 'absolute',
  top: 0,
  left: 0,
  [s.sm_down]: {
    width: '100%',
    height: '100%',
    /* prettier-ignore */
    background: `linear-gradient(to bottom, 
      ${colors.greyDarker} 0%, 
      ${colors.greyDarker.concat(alpha[90])} 15%, 
      ${colors.greyDarker.concat(alpha[60])} 60%,  
      ${colors.greyDarker.concat(alpha[30])} 85%, 
      ${colors.greyDarker.concat(alpha[0])} 100%)`,
  },
  [s.md]: {
    width: '75%',
    height: '100%',
    background: `linear-gradient(to right, ${
      colors.greyDarker
    }, ${colors.greyDarker.concat(alpha[90])} 25%, ${colors.greyDarker.concat(
      alpha[66]
    )} 50%, ${colors.greyDarker.concat(alpha[0])})`,
  },
}

const sButtonTrade = {
  background: 'red',
}

export default Intro
